import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';
import { ChevronDown, ChevronRight, LogOut, Menu as MenuIcon, Moon, Sun } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { getAccess } from '~/modules/access';
import { getToken, signOut } from '~/modules/token';
import globalState, { useAppDispatch } from '~/state/global';
import { routes } from '~/app/routes';
import { buildMenu } from '~/app/nav';

const groupId = (label: string) => `nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`;
// Open state is keyed by section too, so the same group label in two sections stays distinct.
const groupKey = (section: string | undefined, label: string) => `${section}/${label}`;

interface NameParts {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  name?: string;
  email?: string;
}

type ThemeMode = 'dark' | 'light';

interface ThemeState {
  persisted: boolean;
  mode: ThemeMode;
}

const THEME_DARK_CLASS = 'theme-dark';
const THEME_STORAGE_KEY = 'ui-toolbox-theme';
const THEME_OVERRIDE_KEY = 'ui-toolbox-theme-override';

const systemTheme = (): ThemeMode =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const storedTheme = (): ThemeMode | null => {
  try {
    if (localStorage.getItem(THEME_OVERRIDE_KEY) !== 'true') return null;
    const m = localStorage.getItem(THEME_STORAGE_KEY);
    return m === 'dark' || m === 'light' ? m : null;
  } catch {
    return null;
  }
};

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || 'BAY';

const nameFromUser = (user?: NameParts | null): string =>
  [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.fullName || user?.name || '';

// Resolve the logged-in user's display name. The Redux user object (populated at login) is preferred,
// then the decoded bay session token, then the legacy `lastUserFirstName` cookie that the accounts
// service sets and that the previous UI relied on, then email.
const resolveUserName = (userState?: NameParts | null): string => {
  const fromState = nameFromUser(userState);
  if (fromState) return fromState;

  try {
    const token = getToken();
    const fromToken =
      token && nameFromUser(jwt_decode<{ session?: { user?: NameParts } }>(token)?.session?.user);
    if (fromToken) return fromToken;
  } catch {
    /* malformed token: fall through */
  }

  const cookieName = [Cookies.get('lastUserFirstName'), Cookies.get('lastUserLastName')]
    .filter(Boolean)
    .join(' ');
  if (cookieName) return cookieName;

  return userState?.email || 'BAY User';
};

const menuItemClass = (focus: boolean) =>
  `group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left font-medium text-gray-700 text-sm dark:text-gray-300 ${
    focus ? 'bg-gray-100 dark:bg-white/5' : ''
  }`;

export const AppLayout = ({ children }: { children?: ReactNode }) => {
  const location = useLocation();
  const userState = globalState.useUserState() as NameParts | null | undefined;
  const globalDispatch = useAppDispatch();

  const [theme, setTheme] = useState<ThemeState>(() => ({
    persisted: !!storedTheme(),
    mode: storedTheme() ?? systemTheme(),
  }));
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  // Collapsed desktop sidebar shows icons only; hovering temporarily expands it.
  // Page content shifts with `collapsed`, not hover.
  const expanded = !collapsed || sidebarHovered;

  const isDark = theme.mode === 'dark';

  useEffect(() => {
    document.documentElement.classList.toggle(THEME_DARK_CLASS, isDark);
  }, [isDark]);

  useEffect(() => {
    if (!theme.persisted) return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme.mode);
      localStorage.setItem(THEME_OVERRIDE_KEY, 'true');
    } catch {
      /* storage may be unavailable */
    }
  }, [theme]);

  useEffect(() => {
    if (theme.persisted) return;
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) =>
      setTheme((t) => (t.persisted ? t : { persisted: false, mode: e.matches ? 'dark' : 'light' }));
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [theme.persisted]);

  const menu = buildMenu(getAccess());

  const userName = resolveUserName(userState);
  const userEmail = userState?.email || '';

  // Exact for '/' and the Feature flags / AI overviews, which would otherwise match every section under them.
  const isActive = (path: string) =>
    path === routes.dashboard || path === routes.featureFlags || path === routes.ai
      ? location.pathname === path
      : location.pathname.startsWith(path);

  // A group opens by itself when the current path is under one of its sub-items.
  useEffect(() => {
    for (const section of menu) {
      const group = section.items.find((m) => m.subItems?.some((sub) => isActive(sub.path)));
      if (group) setOpenGroup(groupKey(section.label, group.label));
    }
  }, [location.pathname]);

  const toggleTheme = () => setTheme({ persisted: true, mode: isDark ? 'light' : 'dark' });

  const logout = () => {
    globalDispatch(globalState.resetUserState());
    signOut();
  };

  // Labels are always shown in the mobile drawer; only the collapsed desktop rail hides them.
  const hideWhenCollapsed = expanded ? '' : 'lg:hidden';

  return (
    <div className="min-h-screen font-outfit text-gray-800 dark:text-white/90">
      <a
        href="#main-content"
        className="focus-ring sr-only z-60 rounded-lg bg-white px-4 py-2 font-medium text-gray-700 text-sm focus:not-sr-only focus:fixed focus:top-3 focus:left-3 dark:bg-gray-900 dark:text-gray-300"
      >
        Skip to main content
      </a>
      {/* Backdrop renders before the sidebar so the sidebar wins the z-30 tie (DESIGN.md 10.2) */}
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        className={`fixed top-0 left-0 z-30 flex h-screen w-sidebar flex-col border-gray-200 border-r bg-white px-5 transition-all duration-300 dark:border-gray-800 dark:bg-gray-900 lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        } ${expanded ? 'lg:w-sidebar' : 'lg:w-sidebar-collapsed'}`}
      >
        <div className={`flex py-8 ${expanded ? '' : 'lg:justify-center'}`}>
          <Link to={routes.dashboard} onClick={() => setNavOpen(false)}>
            <img
              className={`h-8 w-auto dark:brightness-0 dark:invert ${hideWhenCollapsed}`}
              src="/assets/img/toolbox-logo.png"
              alt="Toolbox"
            />
            {!expanded && (
              <img
                className="hidden h-8 w-auto dark:brightness-0 dark:invert lg:block"
                src="/assets/img/b-logo.png"
                alt="Toolbox"
              />
            )}
          </Link>
        </div>
        <nav aria-label="Main" className="no-scrollbar flex-1 space-y-6 overflow-y-auto pb-6">
          {menu.map((section) => (
            <div key={section.label ?? 'home'}>
              {section.label && (
                <h2 className="mb-4 text-gray-400 text-xs uppercase leading-5">
                  <span className={hideWhenCollapsed}>{section.label}</span>
                  {!expanded && (
                    <span aria-hidden="true" className="hidden lg:inline">
                      •••
                    </span>
                  )}
                </h2>
              )}
              <ul className="space-y-1">
                {section.items.map(({ label, icon: Icon, path, subItems }) => {
                  if (subItems) {
                    const groupActive = subItems.some((sub) => isActive(sub.path));
                    const open = openGroup === groupKey(section.label, label);
                    const listId = groupId(`${section.label ?? 'home'}-${label}`);
                    return (
                      <li key={label}>
                        <button
                          type="button"
                          onClick={() => setOpenGroup(open ? null : groupKey(section.label, label))}
                          aria-expanded={open}
                          aria-controls={listId}
                          title={expanded ? undefined : label}
                          className={`menu-item group cursor-pointer ${groupActive || open ? 'menu-item-active' : 'menu-item-inactive'} ${expanded ? '' : 'lg:justify-center'}`}
                        >
                          <Icon
                            aria-hidden="true"
                            className={`size-5 shrink-0 ${groupActive || open ? 'menu-item-icon-active' : 'menu-item-icon-inactive'}`}
                          />
                          <span className={hideWhenCollapsed}>{label}</span>
                          <ChevronRight
                            aria-hidden="true"
                            className={`ml-auto size-5 transition-transform duration-200 ${hideWhenCollapsed} ${open ? 'rotate-90 text-brand-500 dark:text-brand-400' : ''}`}
                          />
                        </button>
                        {open && (
                          <ul id={listId} className={`mt-2 ml-9 space-y-1 ${hideWhenCollapsed}`}>
                            {subItems.map((sub) => {
                              const subActive = isActive(sub.path);
                              return (
                                <li key={sub.path}>
                                  <Link
                                    to={sub.path}
                                    onClick={() => setNavOpen(false)}
                                    aria-current={subActive ? 'page' : undefined}
                                    className={`menu-dropdown-item ${subActive ? 'menu-dropdown-item-active' : 'menu-dropdown-item-inactive'}`}
                                  >
                                    {sub.label}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  }
                  const active = isActive(path ?? '');
                  return (
                    <li key={path}>
                      <Link
                        to={path ?? routes.dashboard}
                        onClick={() => setNavOpen(false)}
                        title={expanded ? undefined : label}
                        aria-current={active ? 'page' : undefined}
                        className={`menu-item group ${active ? 'menu-item-active' : 'menu-item-inactive'} ${expanded ? '' : 'lg:justify-center'}`}
                      >
                        <Icon
                          aria-hidden="true"
                          className={`size-5 shrink-0 ${active ? 'menu-item-icon-active' : 'menu-item-icon-inactive'}`}
                        />
                        <span className={hideWhenCollapsed}>{label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div
        className={`transition-all duration-300 ${collapsed ? 'lg:pl-sidebar-collapsed' : 'lg:pl-sidebar'}`}
      >
        <header className="sticky top-0 z-20 border-gray-200 border-b bg-white dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center gap-3 px-4 py-3 md:px-6">
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => {
                if (window.innerWidth >= 1024) setCollapsed((c) => !c);
                else setNavOpen(true);
              }}
              className="focus-ring inline-flex size-11 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <MenuIcon aria-hidden="true" className="size-5" />
            </button>

            <div className="ml-auto">
              <Menu as="div" className="relative">
                <MenuButton className="focus-ring flex items-center rounded-lg text-gray-700 dark:text-gray-300">
                  <span className="mr-3 grid size-11 place-items-center rounded-full bg-gray-100 font-semibold text-gray-600 text-sm dark:bg-white/5 dark:text-gray-300">
                    {initials(userName)}
                  </span>
                  <span className="mr-1 hidden font-medium text-gray-700 text-sm dark:text-gray-300 sm:block">
                    {userName}
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className="size-4 text-gray-500 dark:text-gray-400"
                  />
                </MenuButton>

                <MenuItems
                  anchor="bottom end"
                  className="z-60 mt-3 flex w-65 flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-lg focus:outline-none dark:border-gray-800 dark:bg-gray-900"
                >
                  <div className="px-3 pb-3">
                    <span className="block font-medium text-gray-700 text-sm dark:text-gray-300">
                      {userName}
                    </span>
                    {userEmail && (
                      <span className="mt-0.5 block text-gray-500 text-xs dark:text-gray-400">
                        {userEmail}
                      </span>
                    )}
                  </div>
                  <div className="border-gray-200 border-t pt-3 dark:border-gray-800">
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={toggleTheme}
                          className={menuItemClass(focus)}
                        >
                          {isDark ? (
                            <Sun
                              aria-hidden="true"
                              className="size-5 text-gray-500 dark:text-gray-400"
                            />
                          ) : (
                            <Moon
                              aria-hidden="true"
                              className="size-5 text-gray-500 dark:text-gray-400"
                            />
                          )}
                          {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        </button>
                      )}
                    </MenuItem>
                    <MenuItem>
                      {({ focus }) => (
                        <button type="button" onClick={logout} className={menuItemClass(focus)}>
                          <LogOut
                            aria-hidden="true"
                            className="size-5 text-gray-500 dark:text-gray-400"
                          />
                          Sign out
                        </button>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            </div>
          </div>
        </header>

        <main id="main-content" tabIndex={-1} className="p-4 focus:outline-none md:p-6 2xl:p-10">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
